import { qbankClient } from "./qbank";
import { auditClient } from "./audit";
import { caseStudyClient } from "./case-studies";
import { batchClient } from "./batches";
import { platformLibraryClient } from "./platform-library";
import { assessmentClient } from './assessments';
import type {
  ApiEnvelope,
  ApiErrorEnvelope,
  ApiFieldErrors,
  CreateOrganizationInput,
  CreateExamInput,
  UpdateExamStatusInput,
  QbankExam,
  ExamTaxonomy, TaxonomyLevel, TaxonomyNode, CreateTaxonomyNodeInput, UpdateTaxonomyNodeInput,
  InvitationListResult,
  InvitationSummary,
  BatchInvitationFilter,
  OrganizationListQuery,
  OrganizationListResult,
  PlatformOrganization,
  TestEmailResult,
  BrandingAssetKind,
  OrganizationBrandingSettings,
  PlatformOrganizationBrandingSettings,
  SaveStudentThemeInput,
  StudentBranding,
  UpdateOrganizationBrandingInput
} from "@pustak-lms/contracts";

export interface ApiClientOptions {
  baseUrl: string;
}

const mutationListeners = new Set<() => void>();
/** Framework-independent notification after a committed application write. */
export function subscribeApiMutations(listener: () => void): () => void {
  mutationListeners.add(listener);
  return () => { mutationListeners.delete(listener); };
}
export function notifyApiMutation(): void {
  for (const listener of mutationListeners) {
    try { listener(); }
    catch {
      // The server write already committed. A cache subscriber must not turn
      // success into a failed command or prevent other subscribers refreshing.
      console.error("API mutation subscriber failed");
    }
  }
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: ApiFieldErrors;
  readonly requestId: string | undefined;

  constructor(status: number, payload?: ApiErrorEnvelope) {
    super(payload?.error?.message || `API request failed with status ${status}`);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = payload?.error?.code || "REQUEST_FAILED";
    this.fieldErrors = payload?.error?.fieldErrors || {};
    this.requestId = payload?.requestId;
  }
}

export function createApiClient(options: ApiClientOptions) {
  async function requestEnvelope<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");

    if (typeof init.body === "string" && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const response = await fetch(`${options.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: "same-origin",
      cache: "no-store"
    });

    if (!response.ok) {
      let errorPayload: ApiErrorEnvelope | undefined;
      try {
        errorPayload = (await response.json()) as ApiErrorEnvelope;
      } catch {
        // Preserve the status when a proxy or network edge returns non-JSON.
      }
      throw new ApiRequestError(response.status, errorPayload);
    }

    const payload = (await response.json()) as T;
    if (init.method && !["GET", "HEAD"].includes(init.method.toUpperCase())) {
      notifyApiMutation();
    }
    return payload;
  }

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    return (await requestEnvelope<ApiEnvelope<T>>(path, init)).data;
  }

  return {
    ...auditClient(request),
    ...qbankClient(request),
    ...caseStudyClient(request),
    ...batchClient(request),
    ...platformLibraryClient(request),
    ...assessmentClient(request),
    getExamTaxonomy: (examId: string) => request<ExamTaxonomy>(`/api/platform/exams/${encodeURIComponent(examId)}/taxonomy`),
    createTaxonomyNode: (examId: string, level: TaxonomyLevel, body: CreateTaxonomyNodeInput) => request<TaxonomyNode>(`/api/platform/exams/${encodeURIComponent(examId)}/taxonomy/${level}`, {
      method: "POST", body: JSON.stringify(body)
    }),
    updateTaxonomyNode: (examId: string, level: TaxonomyLevel, nodeId: string, body: UpdateTaxonomyNodeInput) => request<TaxonomyNode>(`/api/platform/exams/${encodeURIComponent(examId)}/taxonomy/${level}/${encodeURIComponent(nodeId)}`, {
      method: "POST", body: JSON.stringify(body)
    }),
    listExams: () => request<QbankExam[]>("/api/platform/exams"),
    createExam: (body: CreateExamInput) => request<QbankExam>("/api/platform/exams", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    updateExamStatus: (id: string, body: UpdateExamStatusInput) => request<QbankExam>(`/api/platform/exams/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body: unknown) =>
      request<T>(path, { method: "POST", body: JSON.stringify(body) }),
    getStudentBranding: () => request<StudentBranding>("/api/public/student-branding"),
    getOrganizationBranding: () => request<OrganizationBrandingSettings>("/api/manage/organization/branding"),
    updateOrganizationBranding: (body: UpdateOrganizationBrandingInput) =>
      request<OrganizationBrandingSettings>("/api/manage/organization/branding", {
        method: "PATCH",
        body: JSON.stringify(body)
      }),
    getPlatformOrganizationBranding: (organizationId: string) =>
      request<PlatformOrganizationBrandingSettings>(`/api/platform/organizations/${encodeURIComponent(organizationId)}/branding`),
    savePlatformOrganizationBrandingDraft: (organizationId: string, body: SaveStudentThemeInput) =>
      request<PlatformOrganizationBrandingSettings>(`/api/platform/organizations/${encodeURIComponent(organizationId)}/branding`, {
        method: "PUT", body: JSON.stringify(body)
      }),
    publishPlatformOrganizationBranding: (organizationId: string, body: SaveStudentThemeInput) =>
      request<PlatformOrganizationBrandingSettings>(`/api/platform/organizations/${encodeURIComponent(organizationId)}/branding/publish`, {
        method: "POST", body: JSON.stringify(body)
      }),
    uploadPlatformOrganizationBrandingAsset: (organizationId: string, kind: BrandingAssetKind, file: File) =>
      request<PlatformOrganizationBrandingSettings>(`/api/platform/organizations/${encodeURIComponent(organizationId)}/branding/assets/${kind}?${new URLSearchParams({ name: file.name })}`, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file
      }),
    deletePlatformOrganizationBrandingAsset: (organizationId: string, kind: BrandingAssetKind) =>
      request<PlatformOrganizationBrandingSettings>(`/api/platform/organizations/${encodeURIComponent(organizationId)}/branding/assets/${kind}`, { method: "DELETE" }),
    resetPlatformOrganizationBranding: (organizationId: string) =>
      request<PlatformOrganizationBrandingSettings>(`/api/platform/organizations/${encodeURIComponent(organizationId)}/branding`, { method: "DELETE" }),
    listInvitations: async (endpoint: string, cursor?: string, status: BatchInvitationFilter = "all"): Promise<InvitationListResult> => {
      const parameters = new URLSearchParams();
      if (cursor) parameters.set("cursor", cursor);
      if (status !== "all") parameters.set("status", status);
      const suffix = parameters.size ? `?${parameters}` : "";
      const response = await requestEnvelope<ApiEnvelope<InvitationSummary[]> & { nextCursor: string | null }>(`${endpoint}${suffix}`);
      return { items: response.data, nextCursor: response.nextCursor };
    },
    listOrganizations: (query: OrganizationListQuery = {}) => {
      const parameters = new URLSearchParams();
      if (query.query) parameters.set("query", query.query);
      if (query.limit !== undefined) parameters.set("limit", String(query.limit));
      if (query.cursor) parameters.set("cursor", query.cursor);
      const suffix = parameters.size ? `?${parameters}` : "";
      return request<OrganizationListResult>(`/api/platform/organizations${suffix}`);
    },
    createOrganization: (body: CreateOrganizationInput) =>
      request<PlatformOrganization>("/api/platform/organizations", {
        method: "POST",
        body: JSON.stringify(body)
      }),
    sendTestEmail: () => request<TestEmailResult>("/api/platform/testing/email", {
      method: "POST",
      body: JSON.stringify({})
    })
  };
}

export { createAuthClient, type AuthClient } from "./auth";
